import { createColumnHelper } from "@tanstack/react-table";
import { CONDITION_OPTIONS } from "../BookingForm/constants";
import { type MetadataField } from "../BookingForm/types";
import { type SupplyChainBooking } from "./index";
import { Badge } from "@/components/ui/badge";
import { toDisplayDate } from "@/lib/utils/dates";
import { formatEmptyableValue, formatEnumLabel, formatMetricValue, formatName } from "@/lib/utils/formatting";

const { accessor } = createColumnHelper<SupplyChainBooking>();

/**
 * All metadata column definitions for the bookings table.
 * Each column includes its field ID (matching MetadataField) for visibility filtering.
 */
export const metadataColumns = [
  // Lot & Inventory Details
  accessor((row) => row?.lotNumber, {
    id: "LOT_NUMBER" satisfies MetadataField,
    header: "Lot Number",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: false,
  }),
  accessor((row) => row?.penNumber, {
    id: "PEN_NUMBER" satisfies MetadataField,
    header: "Pen Number",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: false,
  }),
  accessor((row) => row?.accountNumber, {
    id: "ACCOUNT_NUMBER" satisfies MetadataField,
    header: "Contract Number",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: false,
  }),
  accessor((row) => row?.sexClass, {
    id: "SEX_CLASS" satisfies MetadataField,
    header: "Sex Class",
    cell: (info) => {
      const value = info.getValue();
      return value ? formatEnumLabel(value) : "\u2014";
    },
    enableSorting: false,
  }),
  accessor((row) => row?.breedNote, {
    id: "BREED_NOTE" satisfies MetadataField,
    header: "Breed Note",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: false,
  }),
  accessor((row) => row?.accreditations, {
    id: "ACCREDITATIONS" satisfies MetadataField,
    header: "Accreditations",
    cell: (info) => {
      const accreditations = info.getValue();
      if (!accreditations || accreditations.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {accreditations.map((acc) => (
            <Badge
              key={acc.id}
              variant="secondary"
              className="rounded-full gap-1 h-8 px-4 font-medium border-none bg-blue-300/30 text-blue-700"
            >
              {acc.label}
            </Badge>
          ))}
        </div>
      );
    },
    enableSorting: false,
  }),
  accessor((row) => row?.confirmed, {
    id: "CONFIRMED" satisfies MetadataField,
    header: "Status",
    cell: (info) => {
      const value = info.getValue();
      return value === true ? "Confirmed" : value === false ? "Unconfirmed" : "\u2014";
    },
    enableSorting: false,
  }),
  accessor((row) => row?.labels, {
    id: "LABELS" satisfies MetadataField,
    header: "Labels",
    cell: (info) => {
      const labels = info.getValue();
      if (!labels || labels.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {labels.map((label) => (
            <Badge
              key={label.id}
              variant="secondary"
              className="rounded-full gap-1 h-8 px-4 font-medium border-none bg-green-300/30 text-green-700"
            >
              {label.label}
            </Badge>
          ))}
        </div>
      );
    },
    enableSorting: false,
  }),
  accessor((row) => row?.condition, {
    id: "CONDITION" satisfies MetadataField,
    header: "Condition",
    cell: (info) => {
      const value = info.getValue();
      return value ? formatEnumLabel(value) : "\u2014";
    },
    enableSorting: false,
  }),
  accessor((row) => row?.conditions, {
    id: "CONDITIONS" satisfies MetadataField,
    header: "Conditions",
    cell: (info) => {
      const conditions = info.getValue();
      if (!conditions || conditions.length === 0) {
        return "\u2014";
      }
      return (
        <div className="flex flex-wrap gap-1">
          {conditions.map((condition) => {
            const conditionOption = CONDITION_OPTIONS.find((opt) => opt.value === condition);
            const label = conditionOption?.label ?? condition;
            return (
              <Badge
                key={condition}
                variant="secondary"
                className="rounded-full gap-1 h-8 px-4 font-medium border-none bg-slate-300/30 text-slate-700"
              >
                {label}
              </Badge>
            );
          })}
        </div>
      );
    },
    enableSorting: false,
  }),
  accessor((row) => row?.ownershipType, {
    id: "OWNERSHIP_TYPE" satisfies MetadataField,
    header: "Ownership Type",
    cell: (info) => {
      const value = info.getValue();
      return value ? formatEnumLabel(value) : "\u2014";
    },
    enableSorting: false,
  }),

  // Dates & Scheduling
  accessor((row) => row?.dateOfSale, {
    id: "DATE_OF_SALE" satisfies MetadataField,
    header: "Date of Sale",
    cell: (info) => {
      const value = info.getValue();
      return value ? toDisplayDate(value) : "\u2014";
    },
    enableSorting: false,
  }),
  accessor((row) => row?.dateOfVisit, {
    id: "DATE_OF_VISIT" satisfies MetadataField,
    header: "Date of Visit",
    cell: (info) => {
      const value = info.getValue();
      return value ? toDisplayDate(value) : "\u2014";
    },
    enableSorting: false,
  }),
  accessor((row) => row?.projectedOutDate, {
    id: "PROJECTED_OUT_DATE" satisfies MetadataField,
    header: "Projected Out Date",
    cell: (info) => {
      const value = info.getValue();
      return value ? toDisplayDate(value) : "\u2014";
    },
    enableSorting: false,
  }),
  accessor((row) => row?.dateDelivered, {
    id: "DATE_DELIVERED" satisfies MetadataField,
    header: "Date Delivered",
    cell: (info) => {
      const value = info.getValue();
      return value ? toDisplayDate(value) : "\u2014";
    },
    enableSorting: false,
  }),

  // Financial Details
  accessor((row) => row?.price, {
    id: "PRICE" satisfies MetadataField,
    header: "Price",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014", metricType: "currency" }),
    enableSorting: false,
  }),
  accessor((row) => row?.freightCost, {
    id: "FREIGHT_COST" satisfies MetadataField,
    header: "Freight Cost",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014", metricType: "currency" }),
    enableSorting: false,
  }),
  accessor((row) => row?.slide, {
    id: "SLIDE" satisfies MetadataField,
    header: "Slide",
    cell: (info) =>
      formatMetricValue({ value: String(info.getValue() ?? ""), fallbackLabel: "\u2014", metricType: "weight" }),
    enableSorting: false,
  }),
  accessor((row) => row?.purchasePricePerCwt, {
    id: "PURCHASE_PRICE_PER_CWT" satisfies MetadataField,
    header: "Purchase Price/Cwt",
    cell: (info) => formatMetricValue({ value: info.getValue(), fallbackLabel: "\u2014", metricType: "currency" }),
    enableSorting: false,
  }),
  accessor((row) => row?.riskManagement, {
    id: "RISK_MANAGEMENT" satisfies MetadataField,
    header: "Risk Management",
    cell: (info) => {
      const value = info.getValue();
      return value === true ? "Yes" : value === false ? "No" : "\u2014";
    },
    enableSorting: false,
  }),

  // Weight & Quality Information
  accessor((row) => row?.shippedWeight, {
    id: "SHIPPED_WEIGHT" satisfies MetadataField,
    header: "Shipped Weight",
    cell: (info) =>
      formatMetricValue({ value: String(info.getValue() ?? ""), fallbackLabel: "\u2014", metricType: "weight" }),
    enableSorting: false,
  }),
  accessor((row) => row?.baseWeight, {
    id: "BASE_WEIGHT" satisfies MetadataField,
    header: "Base Weight",
    cell: (info) =>
      formatMetricValue({ value: String(info.getValue() ?? ""), fallbackLabel: "\u2014", metricType: "weight" }),
    enableSorting: false,
  }),
  accessor((row) => row?.estimatedChoicePercent, {
    id: "ESTIMATED_CHOICE_PERCENT" satisfies MetadataField,
    header: "Choice %",
    cell: (info) => {
      const value = info.getValue();
      return formatMetricValue({
        value: String(value ? value / 100 : ""),
        fallbackLabel: "\u2014",
        metricType: "percentage",
      });
    },
    enableSorting: false,
  }),
  accessor((row) => row?.shrinkPercent, {
    id: "SHRINK_PERCENT" satisfies MetadataField,
    header: "Shrink %",
    cell: (info) => {
      const value = info.getValue();
      return formatMetricValue({
        value: String(value ? value / 100 : ""),
        fallbackLabel: "\u2014",
        metricType: "percentage",
      });
    },
    enableSorting: false,
  }),
  accessor((row) => row?.estimatedWeightNote, {
    id: "ESTIMATED_WEIGHT_NOTE" satisfies MetadataField,
    header: "Est. Weight Note",
    cell: (info) => {
      const value = info.getValue();
      return value ? <div>{value}</div> : "—";
    },
    enableSorting: false,
  }),

  // Source & Documentation
  accessor((row) => row?.sourceNote, {
    id: "SOURCE_NOTE" satisfies MetadataField,
    header: "Source Note",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: false,
  }),
  accessor((row) => row?.brandNote, {
    id: "BRAND_NOTE" satisfies MetadataField,
    header: "Brand Note",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: false,
  }),
  accessor((row) => row?.certificateNumber, {
    id: "CERTIFICATE_NUMBER" satisfies MetadataField,
    header: "Certificate Number",
    cell: (info) => formatEmptyableValue(info.getValue()),
    enableSorting: false,
  }),
  accessor((row) => row?.program, {
    id: "PROGRAM" satisfies MetadataField,
    header: "Program",
    cell: (info) => formatEmptyableValue(info.getValue()?.label),
    enableSorting: false,
  }),
  accessor((row) => row?.grid, {
    id: "GRID" satisfies MetadataField,
    header: "Grid",
    cell: (info) => formatEmptyableValue(info.getValue()?.label),
    enableSorting: false,
  }),

  // Transportation & Services
  accessor((row) => row?.truckingCompany, {
    id: "TRUCKING_COMPANY" satisfies MetadataField,
    header: "Trucking Company",
    cell: (info) => formatEmptyableValue(info.getValue()?.businessName),
    enableSorting: false,
  }),
  accessor((row) => row?.broker, {
    id: "BROKER" satisfies MetadataField,
    header: "Broker",
    cell: (info) => formatEmptyableValue(info.getValue()?.businessName),
    enableSorting: false,
  }),
  accessor((row) => row?.veterinarian, {
    id: "VETERINARIAN" satisfies MetadataField,
    header: "Veterinarian",
    cell: (info) => {
      const vet = info.getValue();
      return vet?.user ? formatName({ firstName: vet.user.firstName, lastName: vet.user.lastName }) : "\u2014";
    },
    enableSorting: false,
  }),
  accessor((row) => row?.serviceProvided, {
    id: "SERVICE_PROVIDED" satisfies MetadataField,
    header: "Service Provided",
    cell: (info) => {
      const value = info.getValue();
      return value ? <div className="whitespace-pre-wrap break-words">{value}</div> : "—";
    },
    enableSorting: false,
  }),
];
